import React, { useEffect } from 'react'

import { setup, isSupported } from '@loomhq/loom-sdk'
import type {
  SDKButtonInterface,
  SDKUnsupportedReasons,
  SDKState
} from '@loomhq/loom-sdk'

import { REACT_APP_LOOM_API_KEY } from '@pepper/utils/src/api/config'

import { useAsync } from './useAsync'
import type { Status } from './useAsync'
import { useCheckComponentExists } from './useCheckComponentExists'
import { useObjectMemo } from './useObjectMemo'
import { useStableCallback } from './useStableCallback'

export type LoomSDKConfig = {
  bubbleResizable: boolean
  insertButtonText: string
}

type SDKStatusFetcher = () =>
  | {
      state: SDKState
      success: true
    }
  | {
      state: undefined
      success: false
    }

export const LoomRecorderStatus = {
  SUCCESS: 'SUCCESS',
  ELEMENT_NOT_FOUND: 'ELEMENT_NOT_FOUND',
  SDK_UNSUPPORTED: 'SDK_UNSUPPORTED',
  WAITING_SETUP: 'WAITING_SETUP'
} as const

export type LoomRecorderResponse =
  | {
      status: typeof LoomRecorderStatus.ELEMENT_NOT_FOUND
    }
  | {
      status: typeof LoomRecorderStatus.WAITING_SETUP
    }
  | {
      status: typeof LoomRecorderStatus.SDK_UNSUPPORTED
      error: SDKUnsupportedReasons
    }
  | {
      status: typeof LoomRecorderStatus.SUCCESS
      fetchSdkStatus: SDKStatusFetcher
      sdkButton: SDKButtonInterface
    }

const getStatusFetcher = (
  status: () => {
    state: SDKState | undefined
    success: boolean
  }
): SDKStatusFetcher => {
  return () => {
    const response = status()
    if (response.success) {
      return { state: response.state, success: response.success } as {
        state: SDKState
        success: true
      }
    }
    return { state: response.state, success: response.success } as {
      state: undefined
      success: false
    }
  }
}

export const useLoomRecorder = (
  element: HTMLElement | null,
  config?: Partial<LoomSDKConfig>
): [
  (node: unknown) => void,
  {
    setupLoomSDK: () => Promise<void>
    setupFunctionStatus: Status
    setupError: unknown
    setupResult: LoomRecorderResponse | null
  }
] => {
  const memoizedConfig = useObjectMemo<Partial<LoomSDKConfig>>(config ?? {})
  const [isElementRendered, nodeRef] = useCheckComponentExists()

  const setupLoomSDK = useStableCallback(async () => {
    if (!isElementRendered || !element) {
      return {
        status: LoomRecorderStatus.ELEMENT_NOT_FOUND
      }
    }

    const { supported, error } = await isSupported()
    if (!supported) {
      console.warn(`Error setting up Loom: ${error}`)
      return {
        status: LoomRecorderStatus.SDK_UNSUPPORTED,
        error: error as SDKUnsupportedReasons
      }
    }

    const { configureButton, status } = await setup({
      apiKey: REACT_APP_LOOM_API_KEY,
      config: memoizedConfig
    })
    return {
      status: LoomRecorderStatus.SUCCESS,
      sdkButton: configureButton({ element }),
      fetchSdkStatus: getStatusFetcher(status)
    }
  })

  const {
    execute: setupLoom,
    status,
    value,
    error
  } = useAsync<LoomRecorderResponse>(setupLoomSDK, {
    immediate: false,
    valueBeforeAsyncRun: {
      status: LoomRecorderStatus.WAITING_SETUP
    }
  })

  useEffect(() => {
    if (isElementRendered) {
      setupLoom()
    }
  }, [element, memoizedConfig, isElementRendered, setupLoom])

  const loomRecorder = {
    setupLoomSDK: setupLoom,
    setupFunctionStatus: status,
    setupError: error,
    setupResult: value
  }
  return [nodeRef, useObjectMemo(loomRecorder)]
}
